import { z } from 'zod';

import { AgentIdSchema } from '../agent/agent';
import { CardCategorySnapshotSchema } from '../card_category/card_category';
import { CardLabelSnapshotSchema } from '../card_label/card_label';
import { PolicyAccessSchema } from '../policy/policy_access';
import { ProjectIdSchema, SquadIdSchema } from '../project/project';
import { ActorSchema } from '../task/task';

import { CardIdSchema } from './card_id';
import { ClosureSchema } from './closure';
import { HandoffTopicSchema } from './handoff_topic';
import { PrioritySchema } from './priority';

export { CardIdSchema, type CardId } from './card_id';
export { PrioritySchema, type Priority, maxPriority } from './priority';
export { ClosureSchema, ClosureOutcomeSchema, type Closure, type ClosureOutcome } from './closure';

// Origin channel a card was opened on. Chat channels (`whatsapp`, `email`,
// `phone`, `slack`, `sms`, `teams`, `zendesk`, `sunshine`, `app`) mirror the
// conversation's `ChatChannelSchema` so a conversational card faithfully
// reflects where it came from; `voucher` / `ticket` are non-conversational
// origins.
export const CardOriginChannelSchema = z.enum([
  'whatsapp',
  'email',
  'phone',
  'voucher',
  'ticket',
  'slack',
  'sms',
  'teams',
  'zendesk',
  'sunshine',
  'app',
]);

export type CardOriginChannel = z.infer<typeof CardOriginChannelSchema>;

export const CardCustomerSnapshotSchema = z.object({
  id: z.string().max(100),
  name: z.string().max(200),
  vip: z.boolean(),
  // Per-product access flags (`policy-api` policyGetAccessModal). Optional +
  // additive: pre-policy cards/publishers omit it and still validate.
  policyAccess: PolicyAccessSchema.optional(),
});

export type CardCustomerSnapshot = z.infer<typeof CardCustomerSnapshotSchema>;

export const CardCompanySnapshotSchema = z.object({
  id: z.string().max(100),
  name: z.string().max(200),
});

export type CardCompanySnapshot = z.infer<typeof CardCompanySnapshotSchema>;

export const ParticipantRoleSchema = z.enum(['owner', 'collaborator', 'observer']);

export type ParticipantRole = z.infer<typeof ParticipantRoleSchema>;

export const ParticipantAgentKindSchema = z.enum(['agent', 'operator']);

export type ParticipantAgentKind = z.infer<typeof ParticipantAgentKindSchema>;

export const CardParticipantSchema = z.object({
  agentId: AgentIdSchema,
  agentKind: ParticipantAgentKindSchema,
  agentDisplayName: z.string().optional(),
  role: ParticipantRoleSchema,
  addedAt: z.coerce.date(),
  addedByAgentId: AgentIdSchema.nullish(),
  removedAt: z.coerce.date().nullish(),
});

export type CardParticipant = z.infer<typeof CardParticipantSchema>;

/**
 * Input shape accepted by `POST /orgs/:orgId/cards`. The server owns
 * `addedAt` (timestamp) and `removedAt` (mutation-only). `addedByAgentId`
 * stays caller-authoritative so a caller can record `null` (system-add)
 * or another agent's id; the use case falls back to the caller actor
 * when the field is omitted.
 */
export const CardParticipantInputSchema = CardParticipantSchema.omit({
  addedAt: true,
  removedAt: true,
});

export type CardParticipantInput = z.infer<typeof CardParticipantInputSchema>;

export const CardExternalRefSchema = z
  .object({
    system: z.string().max(50),
    type: z.string().max(50).optional(),
    externalId: z.string().max(200),
    snapshot: z
      .record(z.string(), z.unknown())
      .refine((obj) => Object.keys(obj).length <= 20, {
        message: 'snapshot must have at most 20 top-level keys',
      })
      .optional(),
    snapshotAt: z.coerce.date().optional(),
  })
  .superRefine((ref, ctx) => {
    if (ref.snapshot !== undefined && ref.snapshotAt === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['snapshotAt'],
        message: 'snapshotAt is required when snapshot is present',
      });
    }
    if (ref.snapshotAt !== undefined && ref.snapshot === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['snapshot'],
        message: 'snapshot is required when snapshotAt is present',
      });
    }
  });

export type CardExternalRef = z.infer<typeof CardExternalRefSchema>;

export const ChatChannelSchema = z.enum([
  'whatsapp',
  'email',
  'sms',
  'phone',
  'slack',
  'teams',
  'app',
  'zendesk',
  'sunshine',
]);

export type ChatChannel = z.infer<typeof ChatChannelSchema>;

/**
 * Plain object shape of a card — exposed for use by document schemas
 * via `.extend(...)`.
 *
 * Spec v4 retires top-level `status` / `resolution` / `resolvedAt` /
 * `activeAgentIds`: open vs closed is now read from {@link ClosureSchema}
 * and the four columns of the conversation board are derived from
 * `(closure, tasks)` (see Phase 2 board-derivation module).
 *
 * `assigneeAgent` identifies who is responsible for the card. Any kind
 * (`agent` | `operator`) is allowed; `null` means unassigned. The kind is
 * denormalized via the `Actor` envelope so board queries can filter
 * agent vs operator without a `$lookup`.
 */
export const CardSchemaBase = z.object({
  id: CardIdSchema,
  // projectId/squadId/category nullable: card pode existir sem triagem (atribuídos depois).
  projectId: ProjectIdSchema.nullable(),
  squadId: SquadIdSchema.nullable(),
  seq: z.number().int().min(1),
  assigneeAgent: ActorSchema.nullable(),
  category: CardCategorySnapshotSchema.nullable(),
  labels: z.array(CardLabelSnapshotSchema).max(20),
  priority: PrioritySchema,
  originChannel: CardOriginChannelSchema,
  customer: CardCustomerSnapshotSchema,
  company: CardCompanySnapshotSchema,
  participants: z.array(CardParticipantSchema).max(50),
  externalRefs: z.array(CardExternalRefSchema).max(20),
  closure: ClosureSchema.nullable(),
  subject: z.string().max(200).nullable(),
  summary: z.string().max(2000).nullable(),
  // O tema do handoff que o agente classificou. Opcional: cards anteriores ao
  // campo não o têm (a projeção do kanban devolve `null`).
  handoffTopic: HandoffTopicSchema.nullable().optional(),
  createdAt: z.coerce.date(),
});

export const CardSchema = CardSchemaBase.extend({
  /**
   * Display name of the current `assigneeAgent`, resolved at read time
   * from the `agents` collection (never persisted). Populated by the
   * agent-api read paths (`GET /api/v1/cards*`); absent when the card has
   * no assignee or the name can't be resolved. Kept off `CardSchemaBase`
   * so the persisted `CardDocument` and its `$jsonSchema` validator stay
   * unchanged.
   */
  agentDisplayName: z.string().optional(),
  /**
   * Whether the requesting user is the card's human assignee. Resolved at
   * read time on the user-auth card-detail path by comparing
   * `assigneeAgent.id` to the viewer's resolved `AgentId`. Never persisted;
   * absent on read paths without a viewer (e.g. the API-key agent-api).
   */
  assignedToMe: z.boolean().optional(),
});

export type Card = z.infer<typeof CardSchema>;

/**
 * One result row from `GET /orgs/:orgId/search`. Carries a `type`
 * discriminator so the contract is poly-entity from day 1 — when message
 * / contact search lands, this becomes a discriminated union and the
 * frontend doesn't change. Phase 1 has only `card`. `squadId`/`projectId`
 * are nullable: triage cards (no squad assignment) are included in results.
 */
export const CardSearchResultSchema = z.object({
  type: z.literal('card'),
  id: CardIdSchema,
  seq: z.number().int().min(1),
  subject: z.string().max(200).nullable(),
  customerName: z.string().nullable(),
  companyName: z.string().nullable(),
  priority: PrioritySchema,
  isClosed: z.boolean(),
  originChannel: CardOriginChannelSchema,
  squadId: SquadIdSchema.nullable(),
  projectId: ProjectIdSchema.nullable(),
  createdAt: z.coerce.date(),
});
export type CardSearchResult = z.infer<typeof CardSearchResultSchema>;

export const SearchResponseSchema = z.object({
  results: z.array(CardSearchResultSchema),
});
export type SearchResponse = z.infer<typeof SearchResponseSchema>;
